import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import api from "@/lib/api";
import { BookOpen, Video as VideoIcon, Quote } from "lucide-react";

export default function GlobalSearch({ open, onOpenChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ words: [], videos: [], contexts: [] });
  const navigate = useNavigate();

  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 1) {
      setResults({ words: [], videos: [], contexts: [] });
      return;
    }
    try {
      const { data } = await api.get("/search", { params: { q } });
      setResults(data);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runSearch(query), 250);
    return () => clearTimeout(t);
  }, [query, runSearch]);

  const goTo = (path) => {
    onOpenChange(false);
    setQuery("");
    navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Поиск слов, видео, предложений..."
        value={query}
        onValueChange={setQuery}
        data-testid="global-search-input"
      />
      <CommandList data-testid="global-search-results">
        <CommandEmpty>Ничего не найдено</CommandEmpty>
        {results.words?.length > 0 && (
          <CommandGroup heading="Слова">
            {results.words.map((w) => (
              <CommandItem key={w.id} onSelect={() => goTo("/vocabulary")} data-testid={`search-word-${w.id}`}>
                <BookOpen className="w-4 h-4" />
                <span>{w.word}</span>
                <span className="text-muted-foreground">— {w.translation}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.videos?.length > 0 && (
          <CommandGroup heading="Видео">
            {results.videos.map((v) => (
              <CommandItem key={v.id} onSelect={() => goTo(`/player/${v.id}`)} data-testid={`search-video-${v.id}`}>
                <VideoIcon className="w-4 h-4" />
                <span>{v.title}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.contexts?.length > 0 && (
          <CommandGroup heading="Предложения">
            {results.contexts.map((c, i) => (
              <CommandItem key={i} onSelect={() => goTo("/vocabulary")} data-testid={`search-context-${i}`}>
                <Quote className="w-4 h-4" />
                <span className="truncate">{c.sentence}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
